import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { collection, getDocs, query, where, addDoc, serverTimestamp, limit } from "firebase/firestore";
import { db, auth } from "./firebase";
import AppHeader from "./AppHeader";
import { useNavigate } from "react-router-dom";
import { 
  FaMapMarkerAlt, FaCheckCircle, FaHourglassHalf, 
  FaUserTie, FaStore, FaUserGraduate 
} from 'react-icons/fa';
import { MdVerified } from 'react-icons/md'; 

// ---------- Styled Components ----------
const Container = styled.div`
  background-color: #f5f7fa;
  min-height: 100vh;
  padding-bottom: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 2rem;
  color: #1a202c;
  margin: 30px 0;
  font-weight: 700;
  text-align: center;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  width: 95%;
  max-width: 1200px;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const CoverImageWrapper = styled.div`
  height: 100px;
  width: 100%;
  background: #e2e8f0;
`;

const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProfileImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 4px solid white;
  position: absolute;
  top: 60px;
  left: 20px;
  background: white;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 50px 20px 20px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-bottom: 2px;
`;

const Name = styled.h3`
  font-size: 1.25rem;
  color: #2d3748;
  font-weight: 600;
  margin: 0;
`;

const VerifiedIcon = styled(MdVerified)`
  color: #1d9bf0; 
  font-size: 1.2rem;
`;

const BusinessNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #1a73e8;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 6px;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const RoleText = styled.p`
  color: #4a5568;
  font-size: 0.9rem;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ConnectButton = styled.button`
  width: 80%;
  padding: 10px 20px;
  border-radius: 25px;
  border: none;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: auto;
  
  background: ${props => {
    if (props.$status === 'requested') return '#f6ad55'; 
    if (props.$status === 'connected') return '#1a73e8'; 
    return '#1a73e8'; 
  }};
  
  color: white;

  &:hover {
    opacity: 0.9;
    transform: scale(1.02);
  }

  &:disabled {
    cursor: default;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1a73e8;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-top: 50px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const BusinessCorners = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    // Auth status check using onAuthStateChanged to ensure we wait for Firebase
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        // If no user is logged in, redirect to login page
        navigate("/login");
      } else {
        // If user exists, proceed to fetch data
        fetchProfiles(user);
      }
    });

    const fetchProfiles = async (currentUser) => {
      try {
        const q = query(collection(db, "profiles"));
        const querySnapshot = await getDocs(q);
        const fetchedProfiles = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(p => p.uid !== currentUser.uid);
        
        setProfiles(fetchedProfiles);

        const sentQ = query(collection(db, "connections"), where("senderId", "==", currentUser.uid));
        const receivedQ = query(collection(db, "connections"), where("receiverId", "==", currentUser.uid));
        
        const [sentSnap, receivedSnap] = await Promise.all([getDocs(sentQ), getDocs(receivedQ)]);
        
        const statuses = {};
        
        sentSnap.forEach(doc => {
          statuses[doc.data().receiverId] = doc.data().status;
        });

        receivedSnap.forEach(doc => {
          const data = doc.data();
          if (data.status === "connected") {
              statuses[data.senderId] = "connected";
          }
        });

        setConnectionStatuses(statuses);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    return () => unsubscribe();
  }, [navigate]);

  const handleConnect = async (e, targetProfile) => {
    e.stopPropagation();
    const currentUser = auth.currentUser;
    if (!currentUser) return alert("Please login to connect");

    try {
      const q = query(collection(db, "profiles"), where("uid", "==", currentUser.uid), limit(1));
      const snap = await getDocs(q);
      const myData = !snap.empty ? snap.docs[0].data() : {};

      await addDoc(collection(db, "connections"), {
        senderId: currentUser.uid,
        receiverId: targetProfile.uid,
        senderFullName: myData.fullName || "User",
        senderUserName: myData.username || "",
        status: "requested",
        timestamp: serverTimestamp()
      });

      await addDoc(collection(db, "notifications"), {
        receiverId: targetProfile.uid,
        senderId: currentUser.uid,
        senderFullName: myData.fullName || "A user",
        type: "connection_request",
        status: "unread",
        timestamp: serverTimestamp()
      });

      setConnectionStatuses(prev => ({
        ...prev,
        [targetProfile.uid]: 'requested'
      }));
    } catch (error) {
      console.error("Connection error:", error);
    }
  };

  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'student': return <FaUserGraduate />;
      case 'entrepreneur': return <FaUserTie />;
      case 'business': return <FaStore />;
      case 'business owner': return <FaStore />;
      default: return <FaUserTie />;
    }
  };

  return (
    <>
      <AppHeader />
      <Container>
        <Title>Business Corners</Title>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <ProfileGrid>
            {profiles.map((profile) => {
              const status = connectionStatuses[profile.uid] || 'default';
              const isBusiness = profile.mainRole?.toLowerCase().includes('business');

              return (
                <ProfileCard 
                  key={profile.id} 
                  onClick={() => navigate(`/profile/${encodeURIComponent(profile.fullName.replace(/\s+/g, ''))}`)}
                >
                  <CoverImageWrapper>
                    <CoverImage src={profile.coverImageUrl || "/default-cover.jpg"} />
                  </CoverImageWrapper>
                  <ProfileImage src={profile.profileImageUrl || "/default-avatar.png"} />
                  <CardContent>
                    <NameWrapper>
                      <Name>{profile.fullName}</Name>
                      {isBusiness && <VerifiedIcon />}
                    </NameWrapper>

                    {profile.businessName && (
                      <BusinessNameContainer onClick={(e) => {
                        e.stopPropagation(); 
                        
                        const businessSlug = profile.businessName
                          ? profile.businessName.toLowerCase().replace(/[\s-]/g, '')
                          : 'profile';

                        navigate(`/business-view/${businessSlug}`, { 
                          state: { 
                            businessId: profile.uid, 
                            businessName: profile.businessName 
                          } 
                        });
                      }}>
                        <FaStore size={14} /> {profile.businessName}
                      </BusinessNameContainer>
                    )}
                    
                    <RoleText>{getRoleIcon(profile.mainRole)} {profile.mainRole}</RoleText>
                    <p style={{fontSize: '0.85rem', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '5px'}}>
                       <FaMapMarkerAlt /> {profile.city || 'Tamil Nadu'}
                    </p>
                    
                    <ConnectButton 
                      $status={status}
                      disabled={status !== 'default'}
                      onClick={(e) => handleConnect(e, profile)}
                    >
                      {status === 'requested' ? (
                        <><FaHourglassHalf /> Requested</>
                      ) : status === 'connected' ? (
                        <><FaCheckCircle /> Connected</>
                      ) : (
                        'Connect'
                      )}
                    </ConnectButton>
                  </CardContent>
                </ProfileCard>
              );
            })}
          </ProfileGrid>
        )}
      </Container>
    </>
  );
};

export default BusinessCorners;